import { debounce } from '@theme/utilities';

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.indicators = this.querySelector('.indicators');
    this.slides = Array.from(this.slider.children);
    this.firstRealSlide = this.slides[0];
    this.lastRealSlide = this.slides[this.slides.length - 1];
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initLoop();
    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initLoop() {
    if (this.slides.length > 1) {
      const slidesPerView = window.matchMedia('(min-width: 750px)').matches ? 5 : 1;

      const firstRealSlideBeforeClones = this.slides[0];
      const lastRealSlideBeforeClones = this.slides[this.slides.length - 1];
      const isInitialLoad = this.slider.scrollLeft === 0;

      this.firstClones = [];
      for (let i = 0; i < slidesPerView; i++) {
        let clone = this.slides[i].cloneNode(true);
        if (clone.dataset.target) {
          clone.dataset.target = clone.dataset.target + i;
        } else if (clone.dataset.mediaId) {
          clone.dataset.mediaId = clone.dataset.mediaId + i;
        }
        clone.classList.add('is-clone');
        clone.classList.remove('is-active');
        clone.querySelectorAll('img').forEach((img) => {
          if (img.loading === 'eager') {
            img.loading = 'lazy';
          }
          if (img.getAttribute('fetchpriority') === 'high') {
            img.setAttribute('fetchpriority', 'auto');
          }
        });
        this.firstClones.push(clone);
      }

      this.lastClones = [];
      for (let i = this.slides.length - 1; i >= this.slides.length - slidesPerView; i--) {
        let clone = this.slides[i].cloneNode(true);
        if (clone.dataset.target) {
          clone.dataset.target = clone.dataset.target + i;
        } else if (clone.dataset.mediaId) {
          clone.dataset.mediaId = clone.dataset.mediaId + i;
        }
        clone.classList.add('is-clone');
        clone.classList.remove('is-active');
        clone.querySelectorAll('img').forEach((img) => {
          if (img.loading === 'eager') {
            img.loading = 'lazy';
          }
          if (img.getAttribute('fetchpriority') === 'high') {
            img.setAttribute('fetchpriority', 'auto');
          }
        });
        this.lastClones.push(clone);
      }

      this.firstClones.forEach((clone) => this.slider.appendChild(clone));
      this.lastClones.reverse().forEach((clone) => this.slider.insertBefore(clone, this.slides[0]));

      this.firstRealSlide = firstRealSlideBeforeClones;
      this.lastRealSlide = lastRealSlideBeforeClones;

      requestAnimationFrame(() => {
        if (!isInitialLoad) {
          this.slider.style.opacity = '0';
        }

        this.slider.style.scrollSnapType = 'none';
        this.slider.style.scrollBehavior = 'auto';

        const firstRealSlideLeft = this.firstRealSlide.offsetLeft;
        this.slider.scrollLeft = firstRealSlideLeft;

        requestAnimationFrame(() => {
          this.slider.style.scrollSnapType = 'x mandatory';
          this.slider.style.scrollBehavior = 'smooth';

          if (!isInitialLoad) {
            requestAnimationFrame(() => {
              this.slider.style.opacity = '1';
              if (this.indicators) this.indicators.style.opacity = '1';
            });
          } else {
            if (this.indicators) this.indicators.style.opacity = '1';
            if (this.firstRealSlide && !this.firstRealSlide.classList.contains('is-active')) {
              this.firstRealSlide.classList.add('is-active');
            }
          }
        });
      });
    } else {
      const firstSlide = this.sliderItems[0];
      if (firstSlide && !firstSlide.classList.contains('is-active')) {
        firstSlide.classList.add('is-active');
      }
    }
  }

  initPages() {
    requestAnimationFrame(() => {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      const firstItemLeft = this.sliderItemsToShow[0].offsetLeft;
      const secondItemLeft = this.sliderItemsToShow[1].offsetLeft;
      const sliderWidth = this.slider.clientWidth;

      this.sliderItemOffset = secondItemLeft - firstItemLeft;
      this.slidesPerPage = Math.floor((sliderWidth - firstItemLeft) / this.sliderItemOffset);
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    });
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;

    const scrollLeft = this.slider.scrollLeft;
    const previousPage = this.currentPage;
    this.currentPage = Math.round(scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (scrollLeft <= 0) {
      this.slider.style.scrollBehavior = 'auto';
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      requestAnimationFrame(() => {
        if (isDesktop) {
          this.slider.scrollLeft = this.slider.scrollWidth - this.sliderItemOffset * 10;
        } else {
          this.slider.scrollLeft = this.lastRealSlide.offsetLeft;
        }
        requestAnimationFrame(() => (this.slider.style.scrollBehavior = 'smooth'));
      });
    } else if (this.lastRealSlide) {
      const lastRealSlideLeft = this.lastRealSlide.offsetLeft;
      const lastRealSlideWidth = this.lastRealSlide.clientWidth;
      const lastRealSlideRight = lastRealSlideLeft + lastRealSlideWidth;

      if (scrollLeft >= lastRealSlideRight) {
        this.slider.style.scrollBehavior = 'auto';
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        requestAnimationFrame(() => {
          if (isDesktop) {
            this.slider.scrollLeft = this.sliderItemOffset * 5;
          } else {
            this.slider.scrollLeft = this.firstRealSlide.offsetLeft;
          }
          requestAnimationFrame(() => (this.slider.style.scrollBehavior = 'smooth'));
        });
      }
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }
  }

  isSlideVisible(element, offset = 0) {
    const sliderRect = this.slider.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const scrollLeft = this.slider.scrollLeft;
    const lastVisibleSlide = sliderRect.width + scrollLeft - offset;
    const elementLeft = elementRect.left - sliderRect.left + scrollLeft;
    return elementLeft + elementRect.width <= lastVisibleSlide && elementLeft >= scrollLeft;
  }

  isFirstSlideVisible(element, offset = 10) {
    const sliderRect = this.slider.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const scrollLeft = this.slider.scrollLeft;
    const elementLeft = elementRect.left - sliderRect.left + scrollLeft;
    return elementLeft + elementRect.width <= scrollLeft + elementRect.width + offset;
  }

  onButtonClick(event) {
    event.preventDefault();
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.sliderItemOffset : 0 - this.sliderItemOffset;
    this.slider.scrollBy(this.slideScrollPosition, 0);

    const currentSlide = this.slider.querySelector('.thumbnail[aria-current]').closest('.slider__slide');
    const currentIndex = this.slides.indexOf(currentSlide);

    if (event.currentTarget.name === 'next') {
      if (currentIndex === this.slides.length - 1) {
        this.slides[0]?.querySelector('button').dispatchEvent(new Event('click'));
      } else {
        this.slides[currentIndex + 1]?.querySelector('button').dispatchEvent(new Event('click'));
      }
    } else {
      if (currentIndex === 0) {
        this.slides[this.slides.length - 1]?.querySelector('button').dispatchEvent(new Event('click'));
      } else {
        this.slides[currentIndex - 1]?.querySelector('button').dispatchEvent(new Event('click'));
      }
    }
  }

  setSlidePosition(position) {
    this.slider.style.scrollBehavior = 'auto';
    this.slider.scrollLeft = position;
    requestAnimationFrame(() => (this.slider.style.scrollBehavior = 'smooth'));
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    if (!this.slider || !this.nextButton) return;

    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.sliderFirstItemNode = this.slider.querySelector('li');

    if (!this.sliderControlWrapper) return;

    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;
    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    const realSlides = this.enableSliderLooping && this.slides?.length ? this.slides : this.sliderItemsToShow;
    if (!realSlides?.length) return;

    let activeIndex = Math.max(0, (this.currentPage || 1) - 1);
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const activeByClass = realSlides.findIndex((slide) => slide.classList.contains('is-active'));

    if (isDesktop && activeByClass >= 0) {
      activeIndex = activeByClass;
    } else if (this.enableSliderLooping && this.slides?.length) {
      const viewportCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
      let bestDistance = Infinity;
      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(viewportCenter - slideCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          activeIndex = index;
        }
      });
    } else if (activeByClass >= 0) {
      activeIndex = activeByClass;
    }

    realSlides.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      const isActive = index === activeIndex;
      if (isActive) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
        item.inert = false;
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
        item.inert = this.enableSliderLooping ? false : true;
      }
    });

    this.slider?.querySelectorAll('.is-clone').forEach((clone) => {
      clone.setAttribute('aria-hidden', 'true');
    });

    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    requestAnimationFrame(() => {
      const scrollLeft = this.slider.scrollLeft;
      const firstItemWidth = this.sliderFirstItemNode.clientWidth;
      const slideScrollPosition =
        scrollLeft +
        firstItemWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
      this.slider.scrollTo({
        left: slideScrollPosition,
      });
    });
  }
}

customElements.define('slideshow-component-infinite', SlideshowComponent);

if (!customElements.get('media-gallery-infinite')) {
  customElements.define(
    'media-gallery-infinite',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.pauseAllThumbnails();
        this.pauseAllModal();
      }

      onSlideChanged(event) {
        if (event.detail.currentElement) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
          );
          this.setActiveThumbnail(thumbnail);
        }
      }

      pauseAllThumbnails() {
        document.querySelectorAll('.thumbnail-slider li video').forEach((video) => {
          video.pause();
        });
      }

      pauseAllModal() {
        document.querySelectorAll('.product-media-modal__content video').forEach((video) => {
          video.pause();
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not(.is-clone)`) ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        activeMedia.inert = false;
        activeMedia.setAttribute('aria-hidden', 'false');
        activeMedia.removeAttribute('tabindex');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        requestAnimationFrame(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const activeMediaLeft = activeMedia.offsetLeft;
            activeMedia.parentElement.scrollTo({ left: activeMediaLeft });
          }
          requestAnimationFrame(() => {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        const imageSrc = image.src;
        image.src = imageSrc;
      }

      playActiveMedia(activeItem) {
        // window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        const video = activeItem.querySelector('video');
        if (video) video.play();
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    },
  );
}
